import type { CreateProjectApartmentTypeDraft } from "./createDraft";

// Apartment types arrive under many shapes for the SAME physical type:
//   "Type A1 (Studio)"  ==  "A1"  ==  "A1 Studio"
// The identity is the type CODE ("A1", "C1", "S1"); the descriptors
// ("Studio", "1-bed", "(penthouse)") are annotations. We normalise to the
// code so variants collapse to one row, while genuinely different codes
// ("A1" vs "S1") stay separate.

const DESCRIPTOR_WORDS =
  /\b(studio|duplex|penthouse|maisonette|apartment|apt|flat|unit|dwelling|home|bedroom|bedrooms|bed|beds)\b/g;

export function normaliseApartmentTypeKey(name: string): string {
  const raw = (name ?? "").toLowerCase();
  const stripped = raw
    .replace(/\([^)]*\)/g, " ") // drop "(Studio)" / "(1-bedroom)"
    .replace(/\btype\b/g, " ") // drop the literal word "type"
    .replace(/\b\d+\s*-?\s*bed(?:room)?s?\b/g, " ") // drop "1-bed", "2 bedroom"
    .replace(DESCRIPTOR_WORDS, " ");
  const code = stripped.replace(/[^a-z0-9]+/g, "");

  if (code) {
    return code;
  }

  // Pure-descriptor names (e.g. literally "Studio", or a chat-added "1-bed")
  // have no code token: fall back to the whole slug so identical descriptor
  // names still merge, but never collide with a real code.
  return raw.replace(/[^a-z0-9]+/g, "") || "type";
}

const numericStringFields: ReadonlyArray<
  Exclude<
    keyof CreateProjectApartmentTypeDraft,
    "id" | "name" | "provenance" | "wetActuatorCount"
  >
> = [
  "homes",
  "beds",
  "fcuZones",
  "ufhZones",
  "eufhZones",
  "towelRailCount",
  "standaloneHeatOnlyZones",
  "thermostatUiPoints",
  "centralScreens",
  "hiuCount",
  "ciuCount",
  "althermaCount"
];

const hasValue = (value: string | undefined): boolean =>
  typeof value === "string" && value.trim().length > 0;

// Prefer the more descriptive name (longer / carries a parenthetical), so a
// merge of "A1" + "Type A1 (Studio)" keeps the friendlier label.
function richerName(a: string, b: string): string {
  const aHasParen = /\(/.test(a);
  const bHasParen = /\(/.test(b);

  if (aHasParen !== bHasParen) {
    return aHasParen ? a : b;
  }

  return b.trim().length > a.trim().length ? b : a;
}

function mergeDraftRows(
  base: CreateProjectApartmentTypeDraft,
  incoming: CreateProjectApartmentTypeDraft
): CreateProjectApartmentTypeDraft {
  const merged: CreateProjectApartmentTypeDraft = { ...base };

  merged.name = richerName(base.name, incoming.name);

  for (const field of numericStringFields) {
    if (!hasValue(merged[field]) && hasValue(incoming[field])) {
      merged[field] = incoming[field];
    }
  }

  return merged;
}

/**
 * Merge/dedupe draft apartment rows by their normalised type code.
 * Fills missing columns from the richer row and keeps counts. Blank-name
 * rows (manual empty rows) are never merged together.
 */
export function dedupeApartmentDrafts(
  rows: readonly CreateProjectApartmentTypeDraft[]
): CreateProjectApartmentTypeDraft[] {
  const byKey = new Map<string, CreateProjectApartmentTypeDraft>();
  const order: string[] = [];

  for (const row of rows) {
    const key = row.name.trim()
      ? normaliseApartmentTypeKey(row.name)
      : `__blank__${row.id}`;
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, { ...row });
      order.push(key);
      continue;
    }

    byKey.set(key, mergeDraftRows(existing, row));
  }

  return order.map((key) => byKey.get(key)!);
}

/** How many duplicate rows a dedupe would remove (for friendly messaging). */
export function duplicateRowCount(
  rows: readonly CreateProjectApartmentTypeDraft[]
): number {
  return rows.length - dedupeApartmentDrafts(rows).length;
}
